/**
 * Time-sliced (cooperatively scheduled) tiered search over a large corpus —
 * STK-1399.
 *
 * Problem: `tieredSearch` over a 35k-row catalog is a single ~85-175ms
 * synchronous call. Run on (or near) a keystroke it blocks the main thread,
 * so typed characters echo late — no React-level trick (`useDeferredValue`,
 * memoization, component splits) can interrupt one synchronous call.
 *
 * Approach: split the corpus into chunks (default 2,500 rows ≈ 6-10ms of
 * work each). Each chunk gets its own prepared items + Fuse sub-index. A
 * search classifies + fuzzy-scores one chunk at a time, yielding the main
 * thread between chunks (`scheduler.yield()` where available, else
 * `setTimeout(0)`), and checks an `AbortSignal` at every boundary so a new
 * keystroke cancels the in-flight search at the next chunk edge.
 *
 * Result parity: the chunked pipeline runs the exact same steps as the sync
 * `tieredSearch` (shared via `./tieredSearchInternals.ts`). Deterministic
 * classification is per-item, and a Fuse score depends only on
 * (query, item) — never on the rest of the corpus — so the union of per-chunk
 * passes equals one whole-corpus pass. Verified by the parity tests in
 * `__tests__/timeSlicedSearch.test.ts`.
 */
import Fuse from "fuse.js"
import {
  buildQueryVariants,
  classifyPreparedItems,
  collectFuseScores,
  createTierBuckets,
  finalizeTieredResults,
  prepareItems,
  type PreparedItem,
  type TieredSearchConfig,
  type TieredSearchResult,
} from "./tieredSearchInternals"

/**
 * Rows per main-thread slice. Sized so one chunk's classify+fuzzy work stays
 * under a 60fps frame (~6-10ms measured on a 35k-row catalog, worst-case
 * multi-word query) — halving from 5,000 halved the block with no wall-time
 * cost, since yield overhead is amortized by the per-chunk work.
 */
const DEFAULT_CHUNK_SIZE = 2500

export interface TimeSlicedSearchOptions<T> {
  /** Rows per chunk (per main-thread slice). Default 2,500. */
  chunkSize?: number
  /**
   * Minimum trimmed-query length for the fuzzy (tier-6) pass. Below it, only
   * the deterministic tiers (0-5) run. On 1-2 character queries the fuzzy
   * pass costs the most and contributes only noise (threshold-0.4 matches on
   * two letters), so pickers typically set 3. Default 1 (fuzzy always on —
   * matches sync `tieredSearch` behavior).
   *
   * Note: skipping the fuzzy pass also skips fuse scores as the intra-tier
   * secondary sort for those short queries (falls back to the tiebreaker /
   * scan order), which is the trade the caller opts into.
   */
  fuzzyMinQueryLength?: number
  /** Final ordering tiebreaker within equal rank — same as `tieredSearch`'s. */
  tiebreaker?: (a: T, b: T) => number
}

export interface TimeSlicedSearchIndex<T> {
  readonly items: ReadonlyArray<T>
  /**
   * Build all chunk sub-indexes ahead of time, one chunk per main-thread
   * slice. Call on dropdown open so the first keystroke pays zero build cost.
   * Idempotent and resumable: chunks already built are skipped, an aborted
   * warm-up keeps its progress, and `search` builds missing chunks on demand.
   */
  warmUp(opts?: { signal?: AbortSignal }): Promise<void>
  /**
   * Run the full tiered search, yielding between chunks. Rejects with an
   * AbortError (see {@link isAbortError}) when `signal` aborts — callers
   * cancel a stale in-flight search by aborting and issuing a new one.
   */
  search(
    query: string,
    opts?: { signal?: AbortSignal }
  ): Promise<TieredSearchResult<T>[]>
}

function createAbortError(): Error {
  // DOMException is the standard AbortError carrier (browsers, Node 17+);
  // fall back to a name-tagged Error for older runtimes.
  try {
    return new DOMException("The search was aborted", "AbortError")
  } catch {
    const err = new Error("The search was aborted")
    err.name = "AbortError"
    return err
  }
}

/** True for the rejection produced when a time-sliced search is aborted. */
export function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === "AbortError"
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw createAbortError()
}

/** Yield the main thread so queued input events and paints can run. */
function yieldToMain(): Promise<void> {
  const sched = (globalThis as { scheduler?: { yield?: () => Promise<void> } })
    .scheduler
  if (sched && typeof sched.yield === "function") return sched.yield()
  return new Promise((resolve) => setTimeout(resolve, 0))
}

interface BuiltChunk<T> {
  prepared: PreparedItem<T>[]
  fuse: Fuse<T>
}

/**
 * Create a chunked search handle over a fixed corpus. Cheap to construct
 * (no scanning); chunks build lazily via `warmUp`/`search`. Memoize on the
 * corpus array identity and create a fresh index when the catalog changes.
 */
export function createTimeSlicedSearchIndex<T>(
  items: ReadonlyArray<T>,
  config: TieredSearchConfig<T>,
  options: TimeSlicedSearchOptions<T> = {}
): TimeSlicedSearchIndex<T> {
  const chunkSize = Math.max(1, options.chunkSize ?? DEFAULT_CHUNK_SIZE)
  const fuzzyMinQueryLength = options.fuzzyMinQueryLength ?? 1
  const chunkCount = Math.ceil(items.length / chunkSize)
  const built: Array<BuiltChunk<T> | undefined> = new Array(chunkCount)
  const fuseOptions = { ...config.fuseOptions, includeScore: true }

  function ensureChunk(i: number): BuiltChunk<T> {
    let chunk = built[i]
    if (!chunk) {
      const slice = items.slice(i * chunkSize, (i + 1) * chunkSize)
      chunk = {
        prepared: prepareItems(slice, config),
        fuse: new Fuse(slice as T[], fuseOptions),
      }
      built[i] = chunk
    }
    return chunk
  }

  async function warmUp(opts: { signal?: AbortSignal } = {}): Promise<void> {
    const { signal } = opts
    for (let i = 0; i < chunkCount; i++) {
      throwIfAborted(signal)
      ensureChunk(i)
      if (i < chunkCount - 1) await yieldToMain()
    }
  }

  async function search(
    query: string,
    opts: { signal?: AbortSignal } = {}
  ): Promise<TieredSearchResult<T>[]> {
    const { signal } = opts
    throwIfAborted(signal)
    const q = query.trim()
    if (!q) {
      // Mirror sync `tieredSearch`: empty query returns the whole corpus.
      return items.map((item) => ({
        item,
        tier: "exact-field" as const,
        tierIndex: 1,
      }))
    }

    const variantData = buildQueryVariants(q, config.normalizeNumbers !== false)
    const buckets = createTierBuckets<T>()
    const fuseScoreMap = new Map<T, number>()
    const runFuzzy = q.length >= fuzzyMinQueryLength

    for (let i = 0; i < chunkCount; i++) {
      throwIfAborted(signal)
      const chunk = ensureChunk(i)
      classifyPreparedItems(chunk.prepared, variantData, buckets)
      if (runFuzzy) collectFuseScores(chunk.fuse, variantData, fuseScoreMap)
      if (i < chunkCount - 1) await yieldToMain()
    }

    throwIfAborted(signal)
    // Sub-indexes always match the corpus, so no tier-6 stale-cache filter is
    // needed (unlike the sync path's `prebuiltFuse` guard).
    return finalizeTieredResults(
      buckets,
      fuseScoreMap,
      options.tiebreaker,
      null
    )
  }

  return { items, warmUp, search }
}
